import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import { computeFlightPayouts } from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'
import CLUB from '../config/club.js'

// PUBLIC LIVE AUCTION DISPLAY — for the TV.
//   • While bidding: shows the WHOLE selected flight as a board; each team's
//     winning bid + owner fills in live as the admin sells it, and the team the
//     auctioneer is on ("live" pointer) is highlighted.
//   • When the flight is finished: shows the flight-result screen — ONLY the
//     1st/2nd payout amounts plus the day's 1st and 2nd place teams with their
//     owners. MGA (the club's cut) and the gross pot are NEVER shown here (or on
//     any public/owner surface) — MGA is admin-only, on the final reconciliation.
//
//   /calcutta-live?t={tournamentId}
//
const GREEN = '#0E281D'
const GOLD = 'var(--ge-gold)'
const CREAM = 'var(--ge-cream-premium)'

export default function CalcuttaLive({ tournamentIdProp = null, screenProp = null, flightProp = null } = {}) {
  const [params] = useSearchParams()
  const tournamentId = tournamentIdProp || params.get('t')
  // Which screen to render: 'auto' (board while live / result when finished, the
  // standalone default) or an explicit board|spotlight|results|sold|upnext|title
  // (set by the Calcutta screen control per TV, or ?screen= on the URL).
  const screen = screenProp || params.get('screen') || 'auto'
  const flightParam = flightProp || params.get('flight') || null
  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!tournamentId) { setError('Missing tournament ID.'); return }
    const off = onValue(tref(`tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else { setTournament(v); setError('') }
    })
    return () => off()
  }, [tournamentId])

  const cal = tournament?.calcutta || {}
  const live = cal.live || null
  const bids = cal.bids || {}
  const settings = cal.settings || {}
  // Owner names are easy to hide on the board: set calcutta/settings/hideOwners
  // = true and they drop off the live board (still revealed on the result screen).
  const showOwners = !settings.hideOwners

  const flights = useMemo(() => Object.entries(tournament?.flights || {}).map(([id, f]) => ({ id, ...f })), [tournament])
  const flightsSorted = useMemo(() => flights.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)), [flights])
  const activeFlight = (flightParam && flights.find((f) => f.id === flightParam))
    || (live?.flightId && flights.find((f) => f.id === live.flightId)) || flights[0] || null
  const curIdx = activeFlight ? flightsSorted.findIndex((f) => f.id === activeFlight.id) : -1
  const nextFlight = curIdx >= 0 ? (flightsSorted[curIdx + 1] || null) : (flightsSorted[0] || null)
  // 'upnext' targets the NEXT flight; every other screen targets the active one.
  const flight = screen === 'upnext' ? nextFlight : activeFlight
  const teams = useMemo(() => (flight ? Object.entries(flight.teams || {}).map(([id, t]) => ({ id, name: lastNames(t.info?.name) || id, players: t.info?.players || [] })) : []), [flight])
  const isFinished = !!(flight && cal.finished?.[flight.id]?.done)
  const result = (flight && cal.flightResult?.[flight.id]) || null

  const mgaPct = Number.isFinite(Number(settings.mgaPct)) ? Number(settings.mgaPct) : 10
  const structure = Array.isArray(settings.payoutStructure) && settings.payoutStructure.length
    ? settings.payoutStructure : [{ place: 1, pct: 70 }, { place: 2, pct: 30 }]
  const fp = useMemo(
    () => (flight ? (computeFlightPayouts([{ id: flight.id, ...flight }], bids, { mgaPct, payoutStructure: structure, buyBackEnabled: !!settings.buyBackEnabled })[0] || null) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flight, bids, mgaPct, settings.buyBackEnabled],
  )

  if (error) return <Center>{error}</Center>
  if (!tournament) return <Center>Loading…</Center>
  if (screen === 'title') return <TitleScreen flightName={activeFlight?.info?.name} />
  if (!flight) return <Center>{screen === 'upnext' ? 'No next flight — this is the last one.' : 'Waiting for the auction to start…'}</Center>

  switch (screen) {
    case 'board':     return <BoardScreen flight={flight} teams={teams} bids={bids} live={live} fp={fp} showOwners={showOwners} />
    case 'spotlight': return <SpotlightScreen flight={flight} teams={teams} bids={bids} live={live} showOwners={showOwners} />
    case 'results':   return <ResultScreen flight={flight} teams={teams} bids={bids} fp={fp} result={result} />
    case 'sold':      return <SoldScreen flight={flight} teams={teams} bids={bids} showOwners={showOwners} />
    case 'upnext':    return <UpNextScreen flight={flight} teams={teams} />
    default:
      return isFinished
        ? <ResultScreen flight={flight} teams={teams} bids={bids} fp={fp} result={result} />
        : <BoardScreen flight={flight} teams={teams} bids={bids} live={live} fp={fp} showOwners={showOwners} />
  }
}

// ── Team spotlight — the on-the-block team, big ─────────────────────────────
function SpotlightScreen({ flight, teams, bids, live, showOwners }) {
  const t = teams.find((x) => x.id === live?.teamId)
    || teams.find((x) => !(Number(bids[x.id]?.bidAmount) > 0)) || teams[0] || null
  const b = t ? bids[t.id] : null
  const sold = Number(b?.bidAmount) > 0
  return (
    <Shell>
      <Header flightName={flight.info?.name} sub="ON THE BLOCK" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: '7vw', fontWeight: 900, lineHeight: 1.05 }}>{t?.name || '—'}</div>
        {t?.players?.length ? <div style={{ fontSize: '2.6vw', color: 'rgba(247,242,230,0.8)', marginTop: '1vh' }}>{t.players.join(' · ')}</div> : null}
        <div style={{ marginTop: '4vh' }}>
          {sold ? (
            <>
              <div style={{ fontSize: '2vw', color: GOLD, letterSpacing: '0.22em', fontWeight: 800 }}>SOLD</div>
              <div style={{ fontSize: '6vw', fontWeight: 900, color: GOLD }}>${Number(b.bidAmount).toLocaleString()}</div>
              {showOwners ? <div style={{ fontSize: '2.4vw' }}>{b.buyerName}</div> : null}
            </>
          ) : (
            <div style={{ fontSize: '2.6vw', color: GOLD, fontWeight: 800, letterSpacing: '0.15em', border: `3px solid ${GOLD}`, borderRadius: 18, padding: '1.6vh 3.5vw' }}>NOW ON THE BLOCK</div>
          )}
        </div>
      </div>
    </Shell>
  )
}

// ── Sold board — every team with its final price ────────────────────────────
function SoldScreen({ flight, teams, bids, showOwners }) {
  const cols = teams.length > 4 ? 2 : 1
  return (
    <Shell>
      <Header flightName={flight.info?.name} sub="SOLD BOARD" />
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '1.4vh 2vw', alignContent: 'stretch', marginTop: '2vh' }}>
        {teams.map((t) => {
          const b = bids[t.id]; const sold = Number(b?.bidAmount) > 0
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5vw', padding: '1.2vh 1.6vw', borderRadius: 14, background: 'rgba(247,242,230,0.06)', border: '2px solid rgba(247,242,230,0.14)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '2.2vw', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                {sold && showOwners ? <div style={{ fontSize: '1.3vw', color: 'rgba(247,242,230,0.85)' }}>{b.buyerName}</div> : null}
              </div>
              <div style={{ fontSize: '2.4vw', fontWeight: 800, color: sold ? GOLD : 'rgba(247,242,230,0.35)', whiteSpace: 'nowrap' }}>{sold ? `$${Number(b.bidAmount).toLocaleString()}` : '—'}</div>
            </div>
          )
        })}
      </div>
    </Shell>
  )
}

// ── Up next — the next flight coming to the block ───────────────────────────
function UpNextScreen({ flight, teams }) {
  const cols = teams.length > 4 ? 2 : 1
  return (
    <Shell>
      <Header flightName={flight.info?.name} sub="UP NEXT" />
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '1.4vh 2vw', alignContent: 'center', marginTop: '2vh' }}>
        {teams.map((t) => (
          <div key={t.id} style={{ padding: '1.5vh 1.8vw', borderRadius: 14, background: 'rgba(247,242,230,0.06)', border: '2px solid rgba(247,242,230,0.14)', fontSize: '2.4vw', fontWeight: 800, textAlign: 'center' }}>{t.name}</div>
        ))}
      </div>
    </Shell>
  )
}

// ── Title card — clean intro between flights ────────────────────────────────
function TitleScreen({ flightName }) {
  return (
    <Shell>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {CLUB.logoLight ? <img src={CLUB.logoLight} alt={CLUB.name} style={{ height: '22vh', maxWidth: '80vw', objectFit: 'contain', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.4))' }} /> : null}
        <div style={{ color: GOLD, letterSpacing: '0.32em', fontSize: '2.4vw', fontWeight: 800, marginTop: '3vh' }}>CALCUTTA AUCTION</div>
        {flightName ? <div style={{ fontSize: '5vw', fontWeight: 900, marginTop: '1vh' }}>{flightName}</div> : null}
      </div>
    </Shell>
  )
}

// ── While bidding: the whole flight as a board ──────────────────────────────
function BoardScreen({ flight, teams, bids, live, fp, showOwners }) {
  const soldCount = teams.filter((t) => Number(bids[t.id]?.bidAmount) > 0).length
  const cols = teams.length > 4 ? 2 : 1
  return (
    <Shell>
      <Header flightName={flight.info?.name} sub="LIVE AUCTION" />
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '1.4vh 2vw', alignContent: 'stretch', marginTop: '2vh' }}>
        {teams.map((t) => {
          const b = bids[t.id]
          const sold = Number(b?.bidAmount) > 0
          const onBlock = live?.teamId === t.id && !sold
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5vw',
              padding: '1.2vh 1.6vw', borderRadius: 14,
              background: onBlock ? 'rgba(var(--ge-gold-rgb),0.18)' : 'rgba(247,242,230,0.06)',
              border: `2px solid ${onBlock ? GOLD : 'rgba(247,242,230,0.14)'}`,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '2.2vw', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                {onBlock ? <div style={{ fontSize: '1.1vw', color: GOLD, fontWeight: 800, letterSpacing: '0.15em' }}>ON THE BLOCK</div> : null}
              </div>
              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {sold ? (
                  <>
                    <div style={{ fontSize: '2.4vw', fontWeight: 800, color: GOLD }}>${Number(b.bidAmount).toLocaleString()}</div>
                    {showOwners ? <div style={{ fontSize: '1.3vw', color: 'rgba(247,242,230,0.85)' }}>{b.buyerName}</div> : null}
                  </>
                ) : (
                  <div style={{ fontSize: '2vw', color: 'rgba(247,242,230,0.35)' }}>—</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {/* No aggregate POT on the public board — the room sees the per-team sale
          prices (the auction) but no pot total, so MGA can't be backed out by
          comparing a pot to the payouts shown on the result screen. */}
      <div style={{ borderTop: `2px solid ${GOLD}55`, marginTop: '2vh', paddingTop: '1.5vh', display: 'flex', justifyContent: 'center', gap: '4vw', fontSize: '1.9vw' }}>
        <div style={{ color: 'rgba(247,242,230,0.75)' }}>{soldCount} of {teams.length} sold</div>
      </div>
    </Shell>
  )
}

// ── When finished: payout summary + the day's 1st/2nd teams & owners ────────
function ResultScreen({ flight, teams, bids, fp, result }) {
  const nameOf = (teamId) => teams.find((t) => t.id === teamId)?.name || '—'
  const ownerOf = (teamId) => bids[teamId]?.buyerName || '—'
  const firstAmt = fp?.payouts?.[0]?.amount || 0
  const secondAmt = fp?.payouts?.[1]?.amount || 0
  return (
    <Shell>
      <Header flightName={flight.info?.name} sub="FLIGHT RESULT" />
      <div style={{ flex: 1, display: 'flex', gap: '3vw', alignItems: 'stretch', marginTop: '2vh' }}>
        {/* PUBLIC payout summary — ONLY the payouts. MGA (the club's cut) is never
            shown here, and the gross pot is omitted too so MGA can't be derived as
            pot − (1st + 2nd). MGA lives admin-only, on the final reconciliation. */}
        <div style={{ flex: 1, background: 'rgba(247,242,230,0.06)', border: `2px solid ${GOLD}55`, borderRadius: 18, padding: '2.5vh 2vw' }}>
          <div style={{ color: GOLD, letterSpacing: '0.2em', fontWeight: 800, fontSize: '1.5vw' }}>PAYOUT SUMMARY</div>
          <SummaryRow label="1st place" value={firstAmt} big />
          <SummaryRow label="2nd place" value={secondAmt} />
        </div>
        {/* End-of-day result */}
        <div style={{ flex: 1.2, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3vh' }}>
          <PlaceCard medal="🥇" place="1st Place" team={nameOf(result?.firstTeamId)} owner={ownerOf(result?.firstTeamId)} amount={firstAmt} set={!!result?.firstTeamId} />
          <PlaceCard medal="🥈" place="2nd Place" team={nameOf(result?.secondTeamId)} owner={ownerOf(result?.secondTeamId)} amount={secondAmt} set={!!result?.secondTeamId} />
        </div>
      </div>
    </Shell>
  )
}

function PlaceCard({ medal, place, team, owner, amount, set }) {
  return (
    <div style={{ background: 'rgba(var(--ge-gold-rgb),0.12)', border: `2px solid ${GOLD}`, borderRadius: 18, padding: '2.5vh 2.5vw' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '1.6vw', color: GOLD, fontWeight: 800, letterSpacing: '0.15em' }}>{medal} {place.toUpperCase()}</div>
        <div style={{ fontSize: '2.6vw', fontWeight: 800, color: GOLD }}>${Number(amount).toLocaleString()}</div>
      </div>
      {set ? (
        <>
          <div style={{ fontSize: '3.4vw', fontWeight: 800, marginTop: '0.5vh' }}>{team}</div>
          <div style={{ fontSize: '1.8vw', color: 'rgba(247,242,230,0.85)' }}>Owner: <b>{owner}</b></div>
        </>
      ) : (
        <div style={{ fontSize: '2vw', color: 'rgba(247,242,230,0.5)', marginTop: '1vh' }}>Final standings pending…</div>
      )}
    </div>
  )
}

function SummaryRow({ label, value, big, dim }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '1.6vh', color: dim ? 'rgba(247,242,230,0.7)' : CREAM }}>
      <span style={{ fontSize: big ? '2.2vw' : '1.9vw', fontWeight: big ? 800 : 600 }}>{label}</span>
      <span style={{ fontSize: big ? '2.8vw' : '2.2vw', fontWeight: 800, color: big ? GOLD : (dim ? 'rgba(247,242,230,0.7)' : CREAM) }}>${Number(value || 0).toLocaleString()}</span>
    </div>
  )
}

function Header({ flightName, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      {CLUB.logoLight ? <img src={CLUB.logoLight} alt={CLUB.name} style={{ height: '16vh', maxWidth: '80vw', objectFit: 'contain', display: 'block', margin: '0 auto', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.4))' }} /> : null}
      <div style={{ color: GOLD, letterSpacing: '0.28em', fontSize: '1.7vw', fontWeight: 800, marginTop: '0.8vh' }}>CALCUTTA · {sub}</div>
      <div style={{ fontSize: '2.9vw', fontWeight: 800 }}>{flightName || 'Flight'}</div>
    </div>
  )
}

function Shell({ children }) {
  return <div style={{ minHeight: '100vh', height: '100vh', background: GREEN, color: CREAM, fontFamily: 'Georgia, serif', padding: '3vh 3vw', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>{children}</div>
}
function Center({ children }) {
  return <div style={{ minHeight: '100vh', background: GREEN, color: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif', fontSize: '2vw' }}>{children}</div>
}
